import { useChat } from '@ai-sdk/react';
import { useAui, useAuiState } from '@assistant-ui/react';
import { useAISDKRuntime } from '@assistant-ui/react-ai-sdk';
import { AgentChat } from '@cadstrom/cadstrom-shared';
import { useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport } from 'ai';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useTRPC, useTRPCClient } from '#src/trpc.js';
import { useAuth0Context } from '#state/contexts/auth0-context.js';
import { useAgentWorkbenchAnalytics } from './agent-workbench-analytics.js';
import { AgentWorkbenchFeedbackAdapter } from './agent-workbench-feedback-adapter.js';
import { AgentWorkbenchMessageHistoryAdapter } from './agent-workbench-message-history-adapter.js';
import {
	useAgentWorkbenchPageContextAnalytics,
	useAgentWorkbenchPageContextText,
} from './agent-workbench-page-context.js';
import { useAttachmentStoreApi } from './attachment-store-context.js';
import { useWorkbenchRunStatusStoreApi } from './workbench-run-status-context.js';
import { useConversationRunWatch } from './use-conversation-run-watch.js';

import type { ChatTransport, UIMessage } from 'ai';
import type { RefObject } from 'react';
import type { AgentWorkbenchAnalyticsContext } from './agent-workbench-analytics.js';
import type { Attachment } from './attachment-store.js';

/**
 * Delegates every transport call to the latest per-render transport instance,
 * so the Workbench object (which captures its transport at construction) keeps
 * seeing fresh request closures (page context, conversation id).
 */
class LatestWorkbenchTransport implements ChatTransport<UIMessage> {
	public constructor(private readonly holder: RefObject<DefaultChatTransport<UIMessage>>) {}

	public sendMessages(
		...args: Parameters<ChatTransport<UIMessage>['sendMessages']>
	): ReturnType<ChatTransport<UIMessage>['sendMessages']> {
		return this.holder.current.sendMessages(...args);
	}

	public reconnectToStream(
		...args: Parameters<ChatTransport<UIMessage>['reconnectToStream']>
	): ReturnType<ChatTransport<UIMessage>['reconnectToStream']> {
		return this.holder.current.reconnectToStream(...args);
	}
}

interface AgentWorkbenchMessageMetadata {
	model?: {
		provider: string;
		model: string;
	};
	usage?: {
		inputTokens: number;
		outputTokens: number;
		totalTokens: number;
		cachedInputTokens: number;
		cacheCreationTokens: number;
		reasoningTokens: number;
	};
}

type AgentWorkbenchHistoryMessage = UIMessage<AgentWorkbenchMessageMetadata>;

/** Moves API metadata into assistant-ui's supported custom metadata envelope. */
export function prepareHistoryMessages(messages: readonly AgentWorkbenchHistoryMessage[]): UIMessage[] {
	return messages.map((message) => ({
		...message,
		...(message.metadata && { metadata: { custom: message.metadata } }),
	}));
}

export interface UseWorkbenchRuntimeForActiveThreadOptions {
	/**
	 * Attach live to runs started elsewhere — another session or a background
	 * worker — via the run-state long-poll and the watch stream. On by default.
	 */
	watchRemoteRuns?: boolean;
	/**
	 * Analytics identity of the workbench surface this thread is rendered on. Omit
	 * to leave the surface uninstrumented.
	 */
	analytics?: AgentWorkbenchAnalyticsContext;
}

/**
 * Per-thread runtime wired to the agent-workbench SSE endpoint. Message history is
 * loaded through a non-suspending {@link AgentWorkbenchMessageHistoryAdapter} rather
 * than via Suspense: the runtime hook is hosted inside assistant-ui's
 * thread-switch machinery, which awaits it rendering to completion before
 * promoting the selected conversation to the active thread, so suspending here
 * would stall the switch and leave the pane on the empty thread. While mounted
 * on an existing conversation it also watches for runs driven by other
 * processes and streams them into the same panel.
 */
export function useWorkbenchRuntimeForActiveThread(options: UseWorkbenchRuntimeForActiveThreadOptions = {}) {
	const trpc = useTRPC();
	const trpcClient = useTRPCClient();
	const queryClient = useQueryClient();
	const { client: auth0Client } = useAuth0Context();
	const effectiveUserContext = useAgentWorkbenchPageContextText();
	// Scoped to the enclosing workbench pane; a stable handle, so it is safe to
	// capture in the transport closure below.
	const attachmentStore = useAttachmentStoreApi();
	const runStatusStore = useWorkbenchRunStatusStoreApi();
	// The surface's own values win over the page context's, which is registered
	// for the agent's prompt and may be coarser.
	const pageContextAnalytics = useAgentWorkbenchPageContextAnalytics();
	const analyticsContext = useMemo(
		() =>
			options.analytics === undefined ?
				undefined
			:	{
					...options.analytics,
					surface: { ...pageContextAnalytics, ...options.analytics.surface },
				},
		[options.analytics, pageContextAnalytics],
	);
	const workbenchAnalytics = useAgentWorkbenchAnalytics(analyticsContext);

	// Reactive reads: `remoteId` is undefined for "new" threads until
	// initialize() resolves; the run watch below (re)starts once it appears.
	const conversationId = useAuiState((s) => s.threadListItem.remoteId);
	const threadId = useAuiState((s) => s.threadListItem.id);
	// assistant-ui keeps every visited thread's runtime hook mounted, not just the
	// selected one, so this instance must know whether it is the thread on screen.
	const isSelectedThread = useAuiState((s) => s.threads.mainThreadId === s.threadListItem.id);

	// Imperative handle for reading the live remoteId at send time. Refs
	// updated via useEffect would lag by one render cycle, losing the race
	// against assistant-ui calling `prepareSendMessagesRequest` immediately
	// after `initialize` resolves; reading from the live runtime state avoids
	// that window entirely.
	const aui = useAui();

	const historyAdapter = useMemo(
		() =>
			new AgentWorkbenchMessageHistoryAdapter(
				() => aui.threadListItem().getState().remoteId,
				async (activeConversationId): Promise<UIMessage[]> => {
					// A short staleTime lets a freshly mounted thread reuse the conversation
					// route's getMessages preload without a redundant refetch, while still
					// revalidating on later mounts: a conversation can gain messages out of
					// band (continued on another device, appended to by a subagent), so its
					// history must not be cached indefinitely.
					const messages = await queryClient.fetchQuery({
						queryKey: trpc.agentChatConversation.getMessages.queryKey({ conversationId: activeConversationId }),
						queryFn: () => trpcClient.agentChatConversation.getMessages.query({ conversationId: activeConversationId }),
						staleTime: 1000 * 30,
						gcTime: 1000 * 60 * 30,
					});
					// tRPC deserialization drops branded nominal types that are only tracked
					// at compile-time; the runtime shape still matches UIMessage. `select`
					// isn't available on the adapter path, so the assistant-ui metadata
					// normalization is applied here instead.
					return prepareHistoryMessages(messages as AgentWorkbenchHistoryMessage[]);
				},
			),
		[aui, queryClient, trpc, trpcClient],
	);

	// Always registered: an adapter is what makes the rating controls work at all.
	// Whether a rating is reported is the recorder's business, not this one's.
	const feedbackAdapter = useMemo(
		() =>
			new AgentWorkbenchFeedbackAdapter(
				() => aui.threadListItem().getState().remoteId,
				(feedback) => workbenchAnalytics.recordFeedback(feedback),
			),
		[aui, workbenchAnalytics],
	);

	/**
	 * Force-fetches fresh checkpointed history for a specific conversation
	 * (updating the cache) and normalizes it. The id is supplied by the caller —
	 * the run watcher passes the conversation it is scoped to — so a fast thread
	 * switch can never make a still-running watcher load the newly selected
	 * thread's history.
	 */
	const loadHistory = useCallback(
		async (conversationId: string): Promise<UIMessage[]> => {
			const messages = await queryClient.fetchQuery({
				queryKey: trpc.agentChatConversation.getMessages.queryKey({ conversationId }),
				queryFn: () => trpcClient.agentChatConversation.getMessages.query({ conversationId }),
				staleTime: 0,
			});
			return prepareHistoryMessages(messages as AgentWorkbenchHistoryMessage[]);
		},
		[queryClient, trpc, trpcClient],
	);

	const transport = new DefaultChatTransport<UIMessage>({
		api: '/api/v1/agent-chat/stream',
		headers: async () => {
			const token = await auth0Client.getTokenSilently();
			return { Authorization: `Bearer ${token}` };
		},
		// Resuming reconnects to the conversation's watch stream (the AI SDK's
		// GET convention), which replays the active run from its start. Read the
		// remoteId imperatively: resumes only happen for initialised threads.
		prepareReconnectToStreamRequest: () => ({
			api: `/api/v1/agent-chat/${aui.threadListItem().getState().remoteId ?? ''}/stream`,
		}),
		prepareSendMessagesRequest: async ({ messages }) => {
			// Only what we removed from `pending`, so a preparation failure restores
			// exactly that — never anything already preserved under a message id.
			let consumedFromPending: readonly Attachment[] = [];
			try {
				// assistant-ui dispatches to the transport before awaiting the
				// adapter's initialize() — remoteId may still be undefined here.
				// Awaiting initialize() explicitly is safe: calls are idempotent.
				const { remoteId: currentConversationId } = await aui.threadListItem().initialize();

				const lastUserIndex = [...messages].reverse().findIndex((m) => m.role === 'user');
				const lastUserMessageIndex = lastUserIndex === -1 ? -1 : messages.length - 1 - lastUserIndex;
				const lastUserMessage = lastUserMessageIndex >= 0 ? messages[lastUserMessageIndex] : undefined;

				// Consume freshly-composed attachments (clearing the composer preview).
				// On a retry/regenerate the pending queue is already empty, so fall back
				// to the attachments preserved under this message id: a send that fails
				// after preparation leaves them in `sent`, and the retry must resend the
				// files rather than silently drop them.
				consumedFromPending = attachmentStore.getState().consume();
				const messageAttachments: readonly Attachment[] =
					consumedFromPending.length > 0 ? consumedFromPending
					: lastUserMessage ? (attachmentStore.getState().sent.get(lastUserMessage.id) ?? [])
					: [];

				// Associate the attachments with the message id so they render inline
				// with the sent message and survive a failed send for retry.
				if (messageAttachments.length > 0 && lastUserMessage) {
					attachmentStore.getState().storeSent(lastUserMessage.id, messageAttachments);
				}

				workbenchAnalytics.recordMessageSent(messages);

				return {
					body: {
						conversationId: currentConversationId,
						userContext: effectiveUserContext,
						messages: messages.map((m, i) => ({
							role: m.role,
							content: m.parts
								.filter((p): p is { type: 'text'; text: string } => p.type === 'text')
								.map((p) => p.text)
								.join('\n'),
							...(m.role === 'user' &&
								i === lastUserMessageIndex &&
								messageAttachments.length > 0 && {
									attachments: messageAttachments.map((a) => ({
										name: a.name,
										mediaType: a.mediaType,
										data: a.data,
									})),
								}),
						})),
					},
				};
			} catch (error) {
				// Restore attachments removed from the composer so a preparation
				// failure doesn't lose them.
				for (const att of consumedFromPending) {
					attachmentStore.getState().add(att);
				}
				throw error;
			}
		},
	});

	// Keep the ref pointing at this render's transport; the stable delegating
	// transport below reads it only at call time (post-render). Mirrors
	// react-ai-sdk's own `useWorkbenchRuntime` internals, which this hook replaces
	// one layer of.
	const transportRef = useRef(transport);
	// Synced in a layout effect, not a passive one: it flushes during commit,
	// before the browser can dispatch the next event, so the first send after a
	// re-render never delegates to the previous transport and its stale request
	// closures (page context, conversation id).
	useLayoutEffect(() => {
		transportRef.current = transport;
	});
	const [dynamicTransport] = useState(() => new LatestWorkbenchTransport(transportRef));

	// History flows in through the adapter (never via ChatInit.messages), so the
	// Workbench instance starts empty and is hydrated by the runtime.
	const workbench = useChat({
		id: threadId,
		transport: dynamicTransport,
		onFinish: workbenchAnalytics.recordRunSettled,
	});

	// Carry the run lifecycle out to the panel's chrome: assistant-ui hosts this
	// hook as a sibling of the panel, so a store is the only route between them.
	// Only the selected thread may write: the store describes one pane, and the
	// panel shows one thread, so a background thread reporting into it would
	// describe the run of a conversation that is not on screen. `isSelectedThread`
	// is a dependency rather than an early-return guard alone, so becoming selected
	// re-reports and the pane picks up this thread's status even when the status
	// itself has not changed since it was last on screen.
	useEffect(() => {
		if (!isSelectedThread) {
			return;
		}
		runStatusStore.getState().report({ status: workbench.status, errorMessage: workbench.error?.message });
	}, [runStatusStore, workbench.status, workbench.error, isSelectedThread]);

	// Tool call annotations ride the same store route: assistant-ui's converted
	// parts drop `toolMetadata`, so the panel reads them from here instead.
	useEffect(() => {
		if (!isSelectedThread) {
			return;
		}
		runStatusStore.getState().reportToolCallAnnotations(AgentChat.collectToolCallAnnotations(workbench.messages));
	}, [runStatusStore, workbench.messages, isSelectedThread]);

	useConversationRunWatch({
		conversationId,
		workbench,
		enabled: options.watchRemoteRuns ?? true,
		getAuthToken: () => auth0Client.getTokenSilently(),
		loadHistory,
	});

	return useAISDKRuntime(workbench, { adapters: { history: historyAdapter, feedback: feedbackAdapter } });
}
